import React, { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { useSelector, useDispatch } from 'react-redux'
import { Divider, Grid, Stack, Button, Box, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { useNavigate } from 'react-router-dom'
import { setSnackBar } from 'state/ui'
import Loading from 'components/generic/Loading'
import BasicDataGrid from 'components/generic/datagrid'
import DatePicker from 'components/generic/form/DatePicker'
import Input from 'components/generic/form/Input'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import Checkbox from 'components/generic/form/Checkbox'
import { setDecimalPrecision } from 'utils/helpers'
import { resolver } from 'utils/validation/invoicePayment'
import { initialInvoicePaymentData } from 'components/containers/outbound-tickets/utils'
import { useGetInvoiceV3Query, useCreateInvoicePaymentMutation } from 'services/invoices'
import { PAYMENT_METHODS_VALUES } from 'utils/constants'
import { addToDate } from 'utils/data-manipulation/common'
import { roundTo } from 'utils/data-manipulation/valueFormatter'

const useStyles = makeStyles(theme => ({
  root: {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.total-row': {
        backgroundColor: theme.palette.info.lightBg,
        '&:hover': {
          backgroundColor: theme.palette.info.lightBg,
        },
      },
      '&:not(.total-row)': {
        '&.Mui-selected': {
          backgroundColor: 'inherit',
        },
        '&:not(.Mui-selected)': {
          backgroundColor: 'inherit',
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}))

const InvoicePaymentBlade = ({ onFormStateChange, onHideModal }) => {
  const navigate = useNavigate()
  const classes = useStyles()
  const dispatch = useDispatch()
  const adjustmentApiRef = useGridApiRef()
  const invoicePaymentApiRef = useGridApiRef()

  const { settings } = useSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  const [paymentRows, setPaymentRows] = useState([
    { id: 'total', name: 'Total', amountDue: 0, amountPaid: 0, remainingBalance: 0 },
  ])
  const [adjustmentRows] = useState([{ id: 'new-adjustment' }, { id: 'adjustment-total', reason: 'Total', amount: 0 }])
  const [disableAmountPaidField, setDisableAmountPaidField] = useState(false)
  const { invoiceId, invoiceNumber, summaryData, paymentMethod, totalAmountPaid, pricePrepend } = useSelector(
    state => state.ui.formModalProps,
  )
  const balance = roundTo(summaryData?.totalValue - totalAmountPaid, 2)
  /** FIXME / TODO: this probably doesn't need to be called. It is only being used to make sure that an invoice exists. */
  const { data: invoiceData, isFetching: isFetchingInvoice } = useGetInvoiceV3Query({ invoiceId }, { skip: !invoiceId })

  const [createInvoicePayment, { isLoading: isLoadingCreateInvoicePayment }] = useCreateInvoicePaymentMutation()

  const isLoading = isFetchingInvoice || isLoadingCreateInvoicePayment

  const methods = useForm({ defaultValues: initialInvoicePaymentData, resolver })
  const { reset, handleSubmit, control, formState, setValue, getValues, setError } = methods
  const { errors } = formState

  const resetPaymentRows = () =>
    setPaymentRows([
      {
        id: 'total',
        name: 'Total',
        amountDue: summaryData?.totalValue,
        amountPaid: totalAmountPaid,
        remainingBalance: balance,
      },
    ])

  useEffect(() => {
    if (invoiceData && summaryData) {
      reset({
        amountPaid: 0,
        remainingInvoiceBalance: balance,
        payFullAmount: false,
        paymentDate: new Date(),
      })
      resetPaymentRows()
    }
  }, [invoiceData, summaryData])

  const handleSuccess = () => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: 'Payment saved',
      }),
    )
    onHideModal(navigate)
  }

  const handleError = error => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || 'There was an error saving your payment',
      }),
    )
  }

  const formatData = data => ({
    invoiceId,
    paymentDate: data?.paymentDate,
    checkNumber: data?.checkNumber,
    amountPaid: parseFloat(data?.amountPaid),
    paymentType: paymentMethod.toUpperCase() || null,
    notes: data?.notes,
  })

  const formatAdjustmentData = (paymentId, data) => ({
    ...formatData(data),
    amountPaid: parseFloat(data?.balanceAdjustmentAmount),
    paymentType: 'ADJUSTMENT',
    notes: data?.balanceAdjustmentReason,
    paymentId,
  })

  const onSubmit = async data => {
    const formattedData = {
      ...formatData(data),
      adjustment: parseFloat(data?.balanceAdjustmentAmount) || 0,
      adjustmentNotes: data?.balanceAdjustmentReason || '',
    }
    if (formattedData.paymentType === PAYMENT_METHODS_VALUES.CHECK.toUpperCase() && !formattedData.checkNumber) {
      setError('checkNumber', { type: 'custom', message: 'Check number is required' })
    } else {
      await createInvoicePayment({ invoiceId, invoicePaymentRequest: formattedData })
        .unwrap()
        .then(() => handleSuccess())
        .catch(error => handleError(error))
    }
  }

  const paymentColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: '',
        flex: 1,
        sortable: false,
      },
      {
        field: 'amountDue',
        headerName: 'Amount Due',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        sortable: false,
        renderCell: params => {
          const { row = {} } = params
          const { amountDue = 0 } = row
          return `${pricePrepend}${setDecimalPrecision(amountDue, { decimalPrecisionOverride: 2 })}`
        },
      },
      {
        field: 'amountPaid',
        headerName: 'Amount Paid',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        sortable: false,
        renderCell: params => {
          const { row = {} } = params
          const { amountPaid = 0 } = row
          return `${pricePrepend}${setDecimalPrecision(amountPaid, { decimalPrecisionOverride: 2 })}`
        },
      },
      {
        field: 'remainingBalance',
        headerName: 'Remaining Balance',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        sortable: false,
        renderCell: params => {
          const { row = {} } = params
          const { remainingBalance = 0 } = row
          return `${pricePrepend}${setDecimalPrecision(remainingBalance, { decimalPrecisionOverride: 2 })}`
        },
      },
    ],
    [pricePrepend],
  )

  const adjustmentColumns = useMemo(
    () => [
      {
        field: 'reason',
        headerName: 'Reason',
        minWidth: 500,
        sortable: false,
        renderCell: params => {
          const { row = {} } = params
          const { id, reason } = row
          if (id === 'adjustment-total') return reason
          return <EditableCell control={control} name="balanceAdjustmentReason" defaultValue="" isEdit size="small" />
        },
      },
      {
        field: '',
        headerName: '',
        flex: 1,
        sortable: false,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        headerAlign: 'right',
        align: 'right',
        width: 150,
        sortable: false,
        renderCell: params => {
          const { row = {} } = params
          const { id, amount } = row

          if (id === 'adjustment-total')
            return `${pricePrepend}${setDecimalPrecision(amount, { decimalPrecisionOverride: 2 })}`
          return (
            <EditableCell
              control={control}
              name="balanceAdjustmentAmount"
              defaultValue=""
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'balanceAdjustmentAmount',
                  inputType: 'price',
                  pricePrepend,
                  onChange: event => {
                    const adjustmentAmount = parseFloat(event?.target?.value)
                    const remainingBalance = invoicePaymentApiRef.current.getRow('total')?.remainingBalance || 0
                    adjustmentApiRef.current.updateRows([
                      {
                        id: 'adjustment-total',
                        amount: adjustmentAmount,
                      },
                    ])
                    setValue('remainingInvoiceBalance', roundTo(remainingBalance - adjustmentAmount, 2))
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
      },
    ],
    [pricePrepend],
  )

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const handleAmountChange = e => {
    const amountToPay = parseFloat(e?.target?.value)
    const remainingBalance = roundTo(balance - amountToPay, 2)
    if (amountToPay) {
      const adjustment = adjustmentApiRef.current.getRow('adjustment-total').amount || 0
      const paymentUpdated = {
        ...paymentRows[0],
        amountPaid: roundTo(totalAmountPaid + amountToPay, 2),
        remainingBalance,
      }
      setPaymentRows([paymentUpdated])
      setValue('remainingInvoiceBalance', roundTo(remainingBalance - adjustment, 2))
    } else {
      resetPaymentRows()
      setValue('remainingInvoiceBalance', balance)
    }
  }

  return (
    <Stack height="100vh">
      {isLoading && <Loading />}
      <FormModalHeading title={`Record Payment - Invoice #${invoiceNumber}`} backButtonPosition="right" />
      <Divider />
      <Box px={4} py={2} flexGrow={1}>
        <Typography mt={1} variant="h6">
          Payment Information
        </Typography>
        <Grid mt={3}>
          <Stack spacing={2}>
            <DatePicker
              name="paymentDate"
              label="Payment Date"
              data-test="payment-date"
              variant="outlined"
              error={!!errors.paymentDate}
              helperText={errors.paymentDate?.message}
              control={control}
              defaultDate={new Date()}
              sx={{ maxWidth: '350px' }}
              datePickerProps={{
                minDate: lockAccountingPeriod.isLockedForAccounting
                  ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                  : undefined,
              }}
            />
            <Input
              name="checkNumber"
              label="Check #"
              data-test="check-number"
              variant="outlined"
              defaultValue=""
              control={control}
              error={!!errors.checkNumber}
              helperText={errors.checkNumber?.message}
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'checkNumber',
                  inputType: 'number',
                  onChange: () => {},
                  thousandSeparator: false,
                  decimalScale: 0,
                },
              }}
              sx={{ maxWidth: '350px' }}
            />
            <Stack direction="row" spacing={2}>
              <Input
                name="amountPaid"
                label="Amount Paid"
                data-test="amount-paid"
                variant="outlined"
                defaultValue={0}
                disabled={disableAmountPaidField}
                control={control}
                error={!!errors.amount}
                helperText={errors.amount?.message}
                InputProps={{
                  inputComponent: InputFormat,
                  inputProps: {
                    name: 'amountPaid',
                    inputType: 'price',
                    pricePrepend,
                    onChange: () => {},
                  },
                }}
                sx={{ maxWidth: '350px' }}
                onChange={handleAmountChange}
              />
              <Checkbox
                name="payFullAmount"
                label="Pay full amount"
                defaultValue={false}
                control={control}
                onChange={event => {
                  setValue('payFullAmount', event.target.checked)
                  setDisableAmountPaidField(event.target.checked)
                  if (event.target.checked === true) {
                    setValue(
                      'amountPaid',
                      setDecimalPrecision(balance, { shouldFormatNumber: false, decimalPrecisionOverride: 2 }),
                    )
                  } else {
                    const amountPaid = totalAmountPaid + parseFloat(getValues('amountPaid'), 10) || 0
                    const paymentUpdated = {
                      ...paymentRows[0],
                      amountPaid,
                      remainingBalance: summaryData?.grossValue - amountPaid,
                    }
                    setPaymentRows([paymentUpdated])
                  }
                }}
              />
            </Stack>
          </Stack>
        </Grid>
        <Typography mt={3} variant="h6">
          Invoice Payment
        </Typography>
        <BasicDataGrid
          loading={isLoading}
          className={classes.root}
          height="auto"
          apiRef={invoicePaymentApiRef}
          rows={paymentRows}
          columns={paymentColumns}
          disableColumnMenu
          hideFooter
          components={{
            LoadingOverlay: () => null,
          }}
          getRowClassName={params => (params.row.id === 'total' ? 'total-row' : 'normal-row')}
        />
        <Typography mt={3} variant="h6">
          Balance Adjustments
        </Typography>
        <BasicDataGrid
          loading={isLoading}
          className={classes.root}
          height="auto"
          apiRef={adjustmentApiRef}
          rows={adjustmentRows}
          columns={adjustmentColumns}
          onCellClick={(params, event) => {
            event.stopPropagation()

            adjustmentApiRef.current.state.focus.cell = null
          }}
          disableColumnMenu
          hideFooter
          components={{
            LoadingOverlay: () => null,
          }}
          getRowClassName={params => (params.row.id === 'adjustment-total' ? 'total-row' : 'normal-row')}
        />
      </Box>
      <Divider />
      <Stack direction="row" p={2} spacing={2}>
        <Input
          name="notes"
          label="Payment Notes"
          data-test="notes"
          variant="outlined"
          defaultValue=""
          control={control}
          error={!!errors.notes}
          helperText={errors.notes?.message}
        />
        <Input
          name="remainingInvoiceBalance"
          label="Remaining Invoice Balance"
          data-test="remaining-invoice-balance"
          variant="standard"
          disabled
          InputProps={{
            disableUnderline: true,
            inputComponent: InputFormat,
            inputProps: {
              name: 'amountPaid',
              inputType: 'price',
              pricePrepend,
              onChange: () => {},
              fixedDecimalScale: true,
            },
          }}
          sx={{
            textAlign: 'right',
          }}
          defaultValue={0}
          control={control}
          error={!!errors.remainingInvoiceBalance}
          helperText={errors.remainingInvoiceBalance?.message}
        />
      </Stack>
      <Divider />
      <Stack direction="row" p={2} spacing={2}>
        <Button variant="contained" onClick={handleSubmit(onSubmit)}>
          Confirm
        </Button>
        <Button variant="outlined" onClick={() => onHideModal(navigate)}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  )
}

InvoicePaymentBlade.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

InvoicePaymentBlade.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default InvoicePaymentBlade
